import { useEffect, useRef } from 'react'
import useToggle from './useToggle'

/**
 * While `hold` is true, the hook will continue to return the same value.
 * When `hold` becomes false, the hook will return the value passed in.
 * If `releaseDelay` is specified, there will be a delay in switching from
 * the held value to the current value.
 */
export default function useHeldValue<T>(
  value: T,
  hold: boolean | ((current: T, held: T) => boolean),
  options?: {
    releaseDelay?: number,
    default?: T,
  },
): T {
  const { releaseDelay, default: defaultValue } = options ?? {}

  const isHoldingRef = useRef(hold)
  const heldValueRef = useRef<T>(value ?? defaultValue!)
  const { toggle: triggerRerender } = useToggle()

  let isHolding = isHoldingRef.current
  let heldValue = heldValueRef.current

  const shouldHold = typeof hold === 'function' ? hold(value, heldValueRef.current) : hold

  if (shouldHold) {
    isHolding = true
  }
  if (!shouldHold && !releaseDelay) {
    isHolding = false
  }
  if (!isHolding) {
    heldValue = value
  }

  useEffect(() => {
    isHoldingRef.current = isHolding
    heldValueRef.current = heldValue
  })

  useEffect(() => {
    if (releaseDelay && !shouldHold) {
      const timeout = setTimeout(() => {
        const wasHolding = isHoldingRef.current
        isHoldingRef.current = false
        if (wasHolding) {
          triggerRerender()
        }
      }, releaseDelay)
      return () => clearTimeout(timeout)
    }
  }, [shouldHold, releaseDelay, triggerRerender])

  return heldValue
}
